import { useEffect, useState, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Debounced auto-detection of brand mentions in arbitrary text.
 *
 * Returns:
 *   matches:           [{ slug, name, ... }, ...]
 *   suggestedNewSlugs: subset of slugs not yet in `existingSlugs`
 *
 * Usage:
 *   const { suggestedNewSlugs, matches } = useBrandDetection(description, selectedBrandSlugs);
 *   if (suggestedNewSlugs.length > 0) show banner...
 */
export function useBrandDetection(text, existingSlugs = []) {
  const [matches, setMatches] = useState([]);
  const timer = useRef(null);
  const cache = useRef(new Map());

  useEffect(() => {
    if (!text || text.trim().length < 5) {
      setMatches([]);
      return;
    }

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const cacheKey = text.trim().toLowerCase();
      if (cache.current.has(cacheKey)) {
        setMatches(cache.current.get(cacheKey));
        return;
      }
      try {
        const detectRes = await axios.post(`${API}/brand-logos/detect`, { text });
        const slugs = detectRes.data?.matches || [];
        if (slugs.length === 0) {
          cache.current.set(cacheKey, []);
          setMatches([]);
          return;
        }
        const listRes = await axios.get(`${API}/brand-logos`);
        const byId = new Map((listRes.data.brands || []).map((b) => [b.slug, b]));
        const enriched = slugs.map((s) => byId.get(s)).filter(Boolean);
        cache.current.set(cacheKey, enriched);
        setMatches(enriched);
      } catch {
        // Silent — auto-detect failure shouldn't block user input
        setMatches([]);
      }
    }, 500);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [text]);

  const suggestedNewSlugs = matches
    .filter((m) => m.available !== false)  // skip brands that need upload
    .map((m) => m.slug)
    .filter((s) => !existingSlugs.includes(s));

  return { matches, suggestedNewSlugs };
}
